import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { usePlayer } from '../hooks/usePlayer.js'
import { getLegalStatus, acceptLegal } from '../api/levelApi.js'
import { acceptedAtSignup, clearPendingLegal, legalVersions } from '../lib/legal.js'
import LegalCheckbox from './LegalCheckbox.jsx'

/**
 * Pantalla bloqueante de aceptación de Términos y Privacidad. Vive en el
 * shell de la app (no en AuthGuard) para actuar en cuanto hay sesión, en
 * cualquier ruta — /onboarding, donde aterriza un alta nueva, no pasa por
 * AuthGuard.
 *
 * Cubre tres casos con el mismo mecanismo: usuarios dados de alta antes de
 * que existiera la casilla, cambios de versión de los documentos, y altas en
 * las que la aceptación marcada en el registro no llegó hasta aquí. Si la
 * aceptación del registro sí llegó (ver lib/legal.js), se registra sola y
 * esta pantalla no se muestra.
 *
 * La app se pinta mientras se consulta: si la consulta falla no se bloquea a
 * nadie por un fallo nuestro, y se reintenta en la siguiente carga.
 */
export default function LegalGate() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { session, passwordRecovery, signOut } = usePlayer()
  const user = session?.user
  const userId = user?.id

  const [docs, setDocs] = useState(null) // != null → hay que aceptar
  const [checked, setChecked] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    setDocs(null)
    setChecked(false)
    setError('')
    if (!userId) return
    let cancelled = false
    ;(async () => {
      try {
        const status = await getLegalStatus(i18n.language)
        if (cancelled || !status?.missing?.length) return
        if (acceptedAtSignup(status.docs, user)) {
          await acceptLegal({ ...legalVersions(status.docs), source: 'signup', locale: i18n.language })
          clearPendingLegal()
          return
        }
        if (!cancelled) setDocs(status.docs)
      } catch { /* no bloquear: se reintenta en la siguiente carga */ }
    })()
    return () => { cancelled = true }
  }, [userId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Durante el restablecimiento de contraseña hay una sesión temporal: no
  // tapar ese formulario, se pedirá la aceptación al entrar de verdad.
  if (!docs || passwordRecovery) return null

  async function handleAccept() {
    setError('')
    setSaving(true)
    try {
      await acceptLegal({ ...legalVersions(docs), source: 'reaccept', locale: i18n.language })
      clearPendingLegal()
      setDocs(null)
    } catch {
      // Incluye el 409 de "la versión ha cambiado": se vuelve a pedir la
      // vigente para que acepte la que de verdad ve enlazada.
      try {
        const status = await getLegalStatus(i18n.language)
        if (status?.docs) setDocs(status.missing?.length ? status.docs : null)
      } catch { /* ignore */ }
      setError(t('legal.error'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="legal-gate" role="dialog" aria-modal="true" aria-labelledby="legal-gate-title">
      <div className="legal-gate-card">
        <h2 id="legal-gate-title">{t('legal.gateTitle')}</h2>
        <p>{t('legal.gateText')}</p>
        <LegalCheckbox docs={docs} checked={checked} onChange={setChecked} id="legal-gate-accept" />
        {error ? <p className="error-text">{error}</p> : null}
        <button className="button-primary" type="button" disabled={!checked || saving} onClick={handleAccept}>
          {saving ? t('legal.saving') : t('legal.acceptBtn')}
        </button>
        <button
          className="button-text"
          type="button"
          disabled={saving}
          onClick={async () => { await signOut(); navigate('/login') }}
        >
          {t('perfil.logout')}
        </button>
      </div>
    </div>
  )
}
